import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import Logo from '@/components/Logo';
import { useDebounced } from '@/lib/catalogue';
import { useCreerCommande, useCreerWalkIn, useCustomers, type Customer } from '@/lib/orders';

/** Plan §5.3 — sélection du client : recherche par nom, n° ou ville, ou création walk-in. */
export default function NouvelleCommande() {
  const [q, setQ] = useState('');
  const [walkIn, setWalkIn] = useState(false);
  const debounced = useDebounced(q, 250);
  const clients = useCustomers(debounced);
  const creer = useCreerCommande();
  const navigate = useNavigate();

  async function ouvrir(client: Customer) {
    const { order } = await creer.mutateAsync(client.id);
    void navigate(`/order/${order.id}`);
  }

  return (
    <div className="min-h-full">
      <header className="bg-card sticky top-0 z-10 space-y-3 border-b p-4">
        <div className="flex items-center gap-3">
          <Link to="/" className="border-input flex h-14 items-center rounded-lg border px-4 font-medium">
            ← Accueil
          </Link>
          <Logo className="h-9" />
          <h1 className="text-xl font-bold">Nouvelle commande</h1>
        </div>

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nom du client, n° de client ou ville…"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          className="border-input bg-background focus:ring-ring h-16 w-full rounded-lg border px-4 text-lg focus:ring-2 focus:outline-none"
        />
      </header>

      <main className="space-y-3 p-4">
        {creer.isError && (
          <p className="text-destructive font-medium">{(creer.error as Error).message}</p>
        )}

        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {clients.isPending ? 'Recherche…' : `${clients.data?.total ?? 0} client(s)`}
          </p>
          <button
            onClick={() => setWalkIn(!walkIn)}
            className="border-input h-12 rounded-lg border px-4 font-medium"
          >
            {walkIn ? 'Annuler' : 'Client absent de la liste'}
          </button>
        </div>

        {walkIn && <FormulaireWalkIn onCree={ouvrir} />}

        <div className="grid gap-3 lg:grid-cols-2">
          {clients.data?.customers.map((c) => (
            <button
              key={c.id}
              onClick={() => void ouvrir(c)}
              disabled={creer.isPending}
              className="bg-card hover:border-primary/50 active:bg-accent flex min-h-20 w-full flex-col justify-center rounded-xl border p-4 text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-2">
                <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                  {c.custNum}
                </span>
                {c.isWalkIn && (
                  <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                    walk-in
                  </span>
                )}
              </div>
              <p className="mt-1 text-lg font-medium">{c.name}</p>
              {(c.city || c.address) && (
                <p className="text-muted-foreground text-sm">
                  {[c.address, c.city, c.province].filter(Boolean).join(', ')}
                </p>
              )}
            </button>
          ))}
        </div>

        {!clients.isPending && clients.data?.customers.length === 0 && (
          <div className="rounded-xl border border-dashed p-10 text-center">
            <p className="font-medium">Aucun client trouvé.</p>
            <p className="text-muted-foreground mt-1 text-sm">
              Vérifiez l'orthographe, ou créez un client walk-in.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function FormulaireWalkIn({ onCree }: { onCree: (c: Customer) => void }) {
  const [form, setForm] = useState({ name: '', city: '', contactName: '', phone: '' });
  const creer = useCreerWalkIn();

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const { customer } = await creer.mutateAsync(form);
        onCree(customer);
      }}
      className="bg-card space-y-4 rounded-xl border p-4"
    >
      <div>
        <h2 className="font-semibold">Nouveau client sur place</h2>
        <p className="text-muted-foreground text-sm">
          Un numéro <span className="font-mono">WI-…</span> lui sera attribué, à rapprocher de l'ERP
          après le show.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {(
          [
            ['name', 'Nom du client *', true],
            ['city', 'Ville', false],
            ['contactName', 'Personne contact', false],
            ['phone', 'Téléphone', false],
          ] as const
        ).map(([champ, label, requis]) => (
          <label key={champ} className="space-y-1.5">
            <span className="block text-sm font-medium">{label}</span>
            <input
              value={form[champ]}
              onChange={(e) => setForm({ ...form, [champ]: e.target.value })}
              required={requis}
              className="border-input bg-background h-14 w-full rounded-lg border px-3 text-base"
            />
          </label>
        ))}
      </div>

      {creer.isError && <p className="text-destructive text-sm">{(creer.error as Error).message}</p>}

      <button
        type="submit"
        disabled={creer.isPending || !form.name.trim()}
        className="bg-primary text-primary-foreground h-14 rounded-lg px-6 font-semibold disabled:opacity-40"
      >
        {creer.isPending ? 'Création…' : 'Créer et commencer la commande'}
      </button>
    </form>
  );
}
